import {
  LitElement,
  html,
  customElement,
  state,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound } from '@oskecare/fe-utilities/lib/decorators';
import '@oskecare/ui-library/components/input';
import '@oskecare/ui-library/components/modal';
import { customerDataManager, router, tagManager } from '../infrastructure.js';
import { config } from '../config.js';

/**
 * Message shown after customer returns from ID code verification promoting
 * masterSn functionality to encourage customers to set up their master sn so
 * they no longer have to use ID code verification.
 */
@customElement('osk-master-sn-message')
class MasterSnMessage extends LitElement {
  /**
   * @type {boolean}
   */
  @state()
  accessor _shouldShowMessageAgain = true;

  /**
   * @type {boolean}
   */
  @state()
  accessor _isMessageShown = false;

  /**
   * In case customer checked they don't want to be bothered again with masterSN,
   * set localStorage information about it.
   */
  setDoNotShowAgain() {
    if (!this._shouldShowMessageAgain) {
      localStorage.setItem('osk-master-sn-message-hide', true);
    }
  }

  /**
   * Handler for toggling the checkbox communicating whether customer wants the message to be shown again.
   */
  @bound
  handleDoNotShowAgainClick() {
    this._shouldShowMessageAgain = !this._shouldShowMessageAgain;
  }

  /**
   * Handler for customer clicking on not being interested in setting master SN.
   */
  @bound
  handleCloseModal() {
    this.setDoNotShowAgain();
    this._isMessageShown = false;
  }

  /**
   * Handler for customer clicking on set master SN, navigates to master sn management.
   *
   * @param {Event} event
   */
  @bound
  handleSetMasterSn(event) {
    event.preventDefault();
    this.setDoNotShowAgain();
    this._isMessageShown = false;
    router.navigate(config.linkConfiguration.masterSnManagementLink);
  }

  /**
   * Displays masterSn modal in case customer returned from id code verification journey (i.e. osk-idauth-perform is set) with elevated privileges a customer admin
   * and did not express themselves in the past that they don't want to be bothered with masterSn (i.e. osk-master-sn-message-hide is not set).
   *
   * @return {Promise<void>}
   */
  async setIsMessageShown() {
    const { isCustomerAdmin } = (await customerDataManager.getCustomerData()).cnData;
    if (
      sessionStorage.getItem('osk-idauth-perform') &&
      !localStorage.getItem('osk-master-sn-message-hide') &&
      isCustomerAdmin
    ) {
      this._isMessageShown = true;
      tagManager.mhubPush({ event: 'action.impressSetupOption.master' });
    }
    sessionStorage.removeItem('osk-idauth-perform');
  }

  /**
   * Disables shadowRoot for this element.
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Lifecycle function.
   * Called after the element’s DOM has been updated the first time, immediately before `updated` is called.
   */
  firstUpdated() {
    super.firstUpdated();
    this.setIsMessageShown();
  }

  render() {
    if (!this._isMessageShown) {
      return null;
    }

    return html`
      <osk-modal trigger-id="master-sn-modal" @close=${this.handleCloseModal}>
        <h2 slot="modal-header">Zjednodušte si prihlasovanie</h2>
        <div slot="modal-body">
          <p class="mb-large">
            Urobte z tohto čísla Hlavný účet svojich služieb a môžete platiť faktúry alebo upravovať
            služby bez toho, aby ste museli zadávať rodné číslo alebo ID kód.
          </p>
          <osk-input
            type="checkbox"
            input-id="do-not-show-again"
            name="checkbox"
            label="Nabudúce nezobrazovať"
            value=${!this._shouldShowMessageAgain}
            @input-change=${this.handleDoNotShowAgainClick}
          ></osk-input>
        </div>
        <div slot="modal-footer">
          <div class="buttons mb-none">
            <a
              href=${config.linkConfiguration.masterSnManagementLink}
              class="btn btn--primary"
              @click=${this.handleSetMasterSn}
            >
              Nastaviť Hlavný účet
            </a>
            <button
              type="button"
              class="btn"
              data-a11y-dialog-hide="true"
              @click=${this.handleCloseModal}
            >
              Zavrieť
            </button>
          </div>
        </div>
      </osk-modal>
    `;
  }
}

export default MasterSnMessage;
